import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, CircularProgress, FormControl, Grid, MenuItem, OutlinedInput, Paper, Select, Stack, Typography } from '@mui/material';

// Importación de servicios individuales
import { alpha, useTheme } from '@mui/material/styles';
import ProductionVsTargetSimpleChart from '../../ui-component/charts/ProductionVsTargetSimpleChart';
import YieldByHourChart from '../../ui-component/charts/YieldByHourChart';
import DowntimeStackedChart from '../../ui-component/charts/DowntimeStackedChart';
import TimeCycleChart from '../../ui-component/charts/TimeCycleLineChart';
import ParetoDefectTypesChart from '../../ui-component/charts/ParetoDefectTypesChart';
import productionService from '../../services/productionService';
import { FilterBar } from '../../ui-component/production/ProductionCard';
import factoryService from '../../services/admin/factoryService';
import shiftService from '../../services/admin/shiftService';
import ProductionVsTargetChart from '../../ui-component/charts/ProductionVsTargetChart';
import machineService from '../../services/admin/machineService';

const Dashboard = () => {
    const theme = useTheme();
    const [shifts, setShifts] = useState([]);
    const [factories, setFactories] = useState([]);
    const [machines, setMachines] = useState([]);

    const [selectedShiftId, setSelectedShiftId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(productionService.formatDateISO(new Date()));
    const [products, setProducts] = useState([]);
    const [selectedProductIdx, setSelectedProductIdx] = useState(0);
    const [selectedMachineId, setSelectedMachineId] = useState(null);
    const [factoryId, setFactoryId] = useState('');
    const ACCENT = theme.palette.secondary.dark;

    const [statisticData, setStatisticData] = useState({
        timeCyclesData: null,
        defectsData: null,
        downtimesData: null,
        productionVsTargetData: null,
        yieldData: null
    });

    const currentProduct = products[selectedProductIdx] ?? null;

    useEffect(() => {
        factoryService.listEnabledFactories().then((answer) => {
            const list = answer ?? [];
            setFactories(list);
            if (list.length > 0) setFactoryId(list[0].id);
        });

        shiftService.listShiftsEnabled().then((answer) => {
            setShifts(answer);
            if (answer.length > 0) setSelectedShiftId(answer[0].id);
        });

        machineService.listEnabledMachines().then((answer) => {
            const list = answer ?? [];
            setMachines(list);
        });
    }, []);

    const getData = async () => {
        if (!selectedShiftId || !factoryId) return;

        try {
            let request = {
                factoryId,
                shiftId: selectedShiftId,
                startDate: selectedDate,
                machineId: selectedMachineId
            };

            let [responseTimeCycles, responseDefects, responseDowntimes, responseYield, responseProduction] = await Promise.all([
                productionService.getTimeCicleByHour(request),
                productionService.getDefects(request),
                productionService.getDowntimesByHour(request),
                productionService.getYieldByHour(request),
                productionService.getProductionVsTarget(request)
            ]);

            let newData = {};
            if (responseTimeCycles?.success) {
                newData.timeCyclesData = responseTimeCycles.data;
            }

            if (responseDefects?.success) {
                newData.defectsData = responseDefects.data;
            }

            if (responseDowntimes?.success) {
                newData.downtimesData = responseDowntimes.data;
            }

            if (responseYield?.success) {
                newData.yieldData = responseYield.data;
            }

            if (responseProduction?.success) {
                newData.productionVsTargetData = responseProduction.data;

                setProducts(responseProduction.data ?? []);
                setSelectedProductIdx(0);
            }

            // 🔥 UN SOLO UPDATE
            setStatisticData((prev) => ({
                ...prev,
                ...newData
            }));
        } catch (error) {
            console.error(error);
        }
    };

    const filteredMachines = useMemo(() => {
        return machines.filter((m) => m.factory.id === factoryId);
    }, [machines, factoryId]);

    useEffect(() => {
        getData();
    }, [factoryId, selectedShiftId, selectedMachineId, selectedDate]);

    useEffect(() => {
        setSelectedMachineId(null);
    }, [factoryId]);

    return (
        <>
            <Stack direction="column" spacing={2}>
                <Typography variant="h5" sx={{ color: 'gray' }}>
                    Estadísticas de producción
                </Typography>

                <FilterBar>
                    <Box
                        sx={{
                            mx: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1
                        }}
                    >
                        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                                Planta
                            </Typography>
                            {/* Shift chips — same pattern as AssignMachineForm */}
                            <FormControl size="small">
                                <Select
                                    value={factoryId}
                                    onChange={(e) => setFactoryId(e.target.value)}
                                    input={
                                        <OutlinedInput
                                            sx={{
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.secondary.dark,
                                                borderRadius: 2,
                                                '& .MuiSelect-select': {
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: theme.palette.secondary.dark
                                                }
                                            }}
                                        />
                                    }
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme.palette.secondary.dark,
                                                color: 'black',
                                                mt: 1,
                                                borderRadius: 3
                                            }
                                        }
                                    }}
                                >
                                    {factories.map((p) => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                                Turno
                            </Typography>
                            {/* Shift chips — same pattern as AssignMachineForm */}
                            <FormControl size="small">
                                <Select
                                    value={selectedShiftId}
                                    displayEmpty
                                    onChange={(e) => setSelectedShiftId(e.target.value)}
                                    input={
                                        <OutlinedInput
                                            sx={{
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.secondary.dark,
                                                borderRadius: 2,
                                                '& .MuiSelect-select': {
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: theme.palette.secondary.dark
                                                }
                                            }}
                                        />
                                    }
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme.palette.secondary.dark,
                                                color: 'black',
                                                mt: 1,
                                                borderRadius: 3
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value={null} disabled>
                                        Selecciona un turno
                                    </MenuItem>
                                    {shifts.map((p) => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                                Máquina
                            </Typography>
                            {/* Shift chips — same pattern as AssignMachineForm */}
                            <FormControl size="small">
                                <Select
                                    value={selectedMachineId}
                                    displayEmpty
                                    onChange={(e) => setSelectedMachineId(e.target.value)}
                                    input={
                                        <OutlinedInput
                                            sx={{
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.secondary.dark,
                                                borderRadius: 2,
                                                '& .MuiSelect-select': {
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: theme.palette.secondary.dark
                                                }
                                            }}
                                        />
                                    }
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme.palette.secondary.dark,
                                                color: 'black',
                                                mt: 1,
                                                borderRadius: 3
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value={null}>Todas las máquinas</MenuItem>
                                    {filteredMachines.map((p) => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.identifier}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        {/*    /!* Date *!/*/}
                        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold', ml: 1 }}>
                                Fecha
                            </Typography>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                style={{
                                    borderRadius: 8,
                                    border: `1px solid ${ACCENT}`,
                                    padding: '4px 10px',
                                    fontSize: '0.85rem',
                                    background: 'transparent',
                                    color: ACCENT,
                                    outline: 'none',
                                    cursor: 'pointer'
                                }}
                                max={new Date()}
                            />
                        </Stack>
                    </Box>
                </FilterBar>
                <Box>
                    <Box
                        sx={{
                            flex: 1,
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gridAutoRows: '1fr', // ✅ todas del mismo tamaño
                            gap: 2,
                            overflow: 'hidden',
                            minHeight: 0
                        }}
                    >
                        <>
                            {products.length > 1 && (
                                <>
                                    <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                                        <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold', ml: 1 }}>
                                            Producto
                                        </Typography>
                                        <FormControl size="small">
                                            <Select
                                                value={selectedProductIdx}
                                                onChange={(e) => setSelectedProductIdx(e.target.value)}
                                                input={
                                                    <OutlinedInput
                                                        sx={{
                                                            backgroundColor: theme.palette.primary.main,
                                                            color: theme.palette.secondary.dark,
                                                            borderRadius: 2,
                                                            '& .MuiSelect-select': {
                                                                backgroundColor: theme.palette.primary.main,
                                                                color: theme.palette.secondary.dark
                                                            }
                                                        }}
                                                    />
                                                }
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            backgroundColor: theme.palette.secondary.dark,
                                                            color: 'black',
                                                            mt: 1,
                                                            borderRadius: 3
                                                        }
                                                    }
                                                }}
                                            >
                                                {products.map((p, idx) => (
                                                    <MenuItem key={p.productId ?? idx} value={idx}>
                                                        {p.productCode
                                                            ? `${p.productCode} — ${p.productName}`
                                                            : p.productName ?? `Producto ${idx + 1}`}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                </>
                            )}
                            <ProductionVsTargetChart chartData={currentProduct?.series} target={currentProduct?.goal} height={400} />
                        </>

                        <YieldByHourChart data={statisticData.yieldData} height={400} />
                        <DowntimeStackedChart chartData={statisticData.downtimesData} height={400} />
                        <TimeCycleChart chartData={statisticData.timeCyclesData} height={400} />
                    </Box>

                    <Box mt={2}>
                        <ParetoDefectTypesChart chartData={statisticData.defectsData} height={400} />
                    </Box>
                </Box>
            </Stack>
        </>
    );
};

export default Dashboard;
